﻿// Quick-edit profile dropdown — anchored under the user chip in the
// top-right of the desktop shell.
//
// Lets the user change:
//   • First / Last name
//   • Phone
//   • Email  (server requires current password when the email itself
//     changes — we surface a password field only in that case)
//   • Profile photo (compressAvatar â†’ PUT /members/:id/avatar)
//
// Password changes still live on the full Profile page (the security
// boundary + confirm-passwords UI deserves its own screen). A "Full
// profile" link jumps there.
//
// Positioning: the container is `position: relative`; the dropdown is
// absolutely positioned with `right: 0; top: 100%` so it lines up
// beneath the chip on the right edge of the topbar.

import React, { useEffect, useRef, useState } from "react";
import {
  Save, Mail, Phone as PhoneIcon, User as UserIcon,
  Camera, LogOut, ExternalLink, Loader2, Trash2, Lock, BookOpen
} from "lucide-react";
import Avatar from "./Avatar";
import { updateMyProfile, api } from "../api";
import { compressAvatar } from "../utils/avatar";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";

export default function ProfileDropdown({
  user, setUser, state, reload, onSignOut, onOpenFullProfile, onOpenUserGuide, onClose
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const selfMember = state?.members?.find(m => m.id === user.id) || user;

  const initialFirst = user.firstName ?? (user.name || "").split(" ")[0] ?? "";
  const initialLast  = user.lastName  ?? (user.name || "").split(" ").slice(1).join(" ") ?? "";

  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName,  setLastName]  = useState(initialLast);
  const [phone,     setPhone]     = useState(user.phone || "");
  const [email,     setEmail]     = useState(user.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const dirty =
    firstName !== initialFirst ||
    lastName  !== initialLast ||
    phone !== (user.phone || "") ||
    email !== (user.email || "");
  const emailChanged = email !== (user.email || "");
  const emailNeedsPw = emailChanged && !oldPassword.trim();

  async function save(e) {
    e?.preventDefault?.();
    if (!dirty) return;
    if (emailChanged && !oldPassword) {
      setErr("Enter your current password to change your email.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const updated = await updateMyProfile({
        firstName, lastName, phone,
        email: emailChanged ? email : undefined,
        oldPassword: emailChanged ? oldPassword : undefined
      });
      if (updated && setUser) setUser(updated);
      if (emailChanged) setOldPassword("");
      toast.success("Profile updated.");
      reload?.();
    } catch (e2) {
      setErr(e2?.message || "Update failed.");
    } finally { setSaving(false); }
  }

  async function onAvatarPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressAvatar(file, { brand: true });
      await api.uploadAvatar(user.id, dataUrl);
      toast.success("Photo updated.");
      await reload?.();
    } catch (err2) {
      toast.error(err2?.message || "Couldn't upload photo.");
    } finally { setUploading(false); }
  }

  async function removeAvatar() {
    if (!(await confirm("Remove your profile photo?"))) return;
    setUploading(true);
    try {
      await api.deleteAvatar(user.id);
      toast.success("Photo removed.");
      await reload?.();
    } catch (err2) {
      toast.error(err2?.message || "Couldn't remove photo.");
    } finally { setUploading(false); }
  }

  const roleLine = (user.roles || [])
    .filter(r => r !== "MEMBER")
    .map(r => r.replace("_", " "))
    .join(" · ");

  return (
    <div
      className="profileDropdown"
      role="dialog"
      aria-label="Your profile"
      onClick={e => e.stopPropagation()}
    >
      {/* Little arrow pointing back up to the chip */}
      <span className="profileDropdown-tail" aria-hidden="true"/>

      <header className="profileDropdown-head">
        <div className="profileDropdown-avatar">
          <Avatar
            member={selfMember}
            size={64}
            style={{ width: 64, height: 64 }}
          />
          <button
            type="button"
            className="profileDropdown-avatarEdit"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Change photo"
            aria-label="Change photo"
          >
            {uploading ? <Loader2 size={12} className="profileDropdown-spin"/> : <Camera size={12}/>}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onAvatarPicked}
            style={{ display: "none" }}
          />
        </div>
        <div className="profileDropdown-headText">
          <h3>{`${firstName} ${lastName}`.trim() || user.name}</h3>
          {roleLine && <small>{roleLine}</small>}
        </div>
        {selfMember?.avatarUpdatedAt && (
          <button
            type="button"
            className="profileDropdown-avatarRemove"
            onClick={removeAvatar}
            disabled={uploading}
            title="Remove photo"
            aria-label="Remove photo"
          ><Trash2 size={13}/></button>
        )}
      </header>

      <form className="profileDropdown-form" onSubmit={save}>
        <div className="profileDropdown-row profileDropdown-row--two">
          <label>
            <span><UserIcon size={11}/> First name</span>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}/>
          </label>
          <label>
            <span>Last name</span>
            <input value={lastName} onChange={e => setLastName(e.target.value)}/>
          </label>
        </div>
        <label className="profileDropdown-row">
          <span><PhoneIcon size={11}/> Phone</span>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 555-5555"/>
        </label>
        <label className="profileDropdown-row">
          <span><Mail size={11}/> Email</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}/>
          {emailChanged && (
            <small className="profileDropdown-warn">
              Changing your email needs your current password below.
            </small>
          )}
        </label>
        {emailChanged && (
          <label className="profileDropdown-row">
            <span><Lock size={11}/> Current password</span>
            <input
              type="password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
        )}

        {err && <div className="profileDropdown-err">{err}</div>}

        <div className="profileDropdown-actions">
          <button
            type="button"
            className="profileDropdown-linkBtn"
            onClick={() => { onClose?.(); onOpenFullProfile?.(); }}
          >
            <ExternalLink size={12}/> Full profile &amp; password
          </button>
          <button
            type="submit"
            className="profileDropdown-saveBtn"
            disabled={!dirty || saving || emailNeedsPw}
          >
            {saving
              ? <><Loader2 size={13} className="profileDropdown-spin"/> Saving…</>
              : <><Save size={13}/> Save</>}
          </button>
        </div>
      </form>

      <div className="profileDropdown-foot">
        {onOpenUserGuide && (
          <button
            type="button"
            className="profileDropdown-linkBtn profileDropdown-linkBtn--full"
            onClick={() => { onClose?.(); onOpenUserGuide?.(); }}
          >
            <BookOpen size={13}/> Open User Guide
          </button>
        )}
        <button
          type="button"
          className="profileDropdown-signOut"
          onClick={() => { onClose?.(); onSignOut?.(); }}
        >
          <LogOut size={13}/> Sign out
        </button>
      </div>
    </div>
  );
}
